import React, { useState, useEffect } from 'react';
import { Box, IconButton, Tooltip, Zoom, Stack, alpha, useTheme } from '@mui/material';
import {
    ExpandMore as ExpandIcon,
    ExpandLess as CollapseIcon
} from '@mui/icons-material';

/**
 * FloatingIconsManager - Gerencia os ícones flutuantes do canto direito
 * Permite recolher os ícones para uma barra horizontal no topo
 */
const FloatingIconsManager = ({ children }) => {
    const theme = useTheme();
    const [isCollapsed, setIsCollapsed] = useState(() => {
        const saved = localStorage.getItem('sach-icons-collapsed');
        return saved === 'true';
    });

    useEffect(() => {
        localStorage.setItem('sach-icons-collapsed', isCollapsed.toString());
    }, [isCollapsed]);

    const toggleCollapse = () => {
        setIsCollapsed(!isCollapsed);
    };

    // Se colapsado, mostrar na parte superior
    if (isCollapsed) {
        return (
            <>
                {/* Barra horizontal no topo direito */}
                <Box
                    sx={{
                        position: 'fixed',
                        top: 70, // Abaixo do header principal
                        right: 16,
                        zIndex: 1299,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        p: 0.5,
                        bgcolor: alpha(theme.palette.background.paper, 0.9),
                        backdropFilter: 'blur(10px)',
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        boxShadow: `0 4px 20px ${alpha('#000', 0.1)}`
                    }}
                >
                    {/* Renderiza os children como ícones horizontais */}
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        {React.Children.map(children, (child) => {
                            if (!child) return null;
                            // Clona o child removendo estilos de posição fixa
                            return React.cloneElement(child, {
                                isCollapsed: true,
                                compact: true
                            });
                        })}
                    </Stack>

                    {/* Botão para expandir */}
                    <Tooltip title="Expandir para lateral" placement="bottom">
                        <IconButton
                            onClick={toggleCollapse}
                            size="small"
                            sx={{
                                width: 28,
                                height: 28,
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                color: theme.palette.primary.main,
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.2)
                                }
                            }}
                        >
                            <ExpandIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </>
        );
    }

    // Layout expandido (lateral direita)
    return (
        <>
            {/* Renderiza normalmente na lateral */}
            {children}

            {/* Botão para recolher */}
            <Zoom in={!isCollapsed}>
                <Tooltip title="Recolher para topo" placement="left">
                    <IconButton
                        onClick={toggleCollapse}
                        size="small"
                        sx={{
                            position: 'fixed',
                            bottom: 240,
                            right: 24,
                            zIndex: 1299,
                            width: 32,
                            height: 32,
                            bgcolor: alpha(theme.palette.grey[500], 0.1),
                            backdropFilter: 'blur(8px)',
                            border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                            color: theme.palette.text.secondary,
                            '&:hover': {
                                bgcolor: alpha(theme.palette.grey[500], 0.2),
                                color: theme.palette.primary.main
                            },
                            transition: 'all 0.3s ease'
                        }}
                    >
                        <CollapseIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Zoom>
        </>
    );
};

export default FloatingIconsManager;
